/**
 * Tabela de itens da entrada com ações de editar e excluir
 */

import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2 } from "lucide-react";
import { ItemEntrada } from "@/types/entrada";
import { formatCurrency } from "@/lib/utils";
import { entradaAdapter } from "@/data";
import { toast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface EntradaItensTableProps {
  itens: ItemEntrada[];
  onEdit: (item: ItemEntrada) => void;
  onRefresh: () => void;
  readOnly?: boolean;
}

export function EntradaItensTable({ itens, onEdit, onRefresh, readOnly = false }: EntradaItensTableProps) {
  const [deletingItem, setDeletingItem] = useState<ItemEntrada | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deletingItem?.id) return;

    try {
      setIsDeleting(true);
      await entradaAdapter.deleteItem(deletingItem.id);
      toast({
        title: "Item removido",
        description: "Item removido da entrada com sucesso.",
      });
      setDeletingItem(null);
      onRefresh();
    } catch (error: any) {
      toast({
        title: "Erro ao remover item",
        description: error?.message || "Não foi possível remover o item",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produto</TableHead>
              <TableHead className="text-right">Quantidade</TableHead>
              <TableHead className="text-right">Custo Unitário</TableHead>
              <TableHead className="text-right">Outras Despesas</TableHead>
              <TableHead className="text-right">Subtotal</TableHead>
              <TableHead>Lote</TableHead>
              {!readOnly && <TableHead className="text-right">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {itens.map((item) => {
              const subtotal = item.quantidade_compra * (item.custo_unitario_compra + (item.outras_despesas_item || 0));
              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{item.produto_nome}</p>
                      {item.marca?.nome && (
                        <p className="text-xs text-muted-foreground">Marca: {item.marca.nome}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div>
                      <p>{item.quantidade_compra}</p>
                      <p className="text-xs text-muted-foreground">{item.tipo_embalagem}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(item.custo_unitario_compra)}</TableCell>
                  <TableCell className="text-right">
                    {item.outras_despesas_item ? formatCurrency(item.outras_despesas_item) : "-"}
                  </TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(subtotal)}</TableCell>
                  <TableCell>
                    {item.lote ? (
                      <Badge variant="outline">{item.lote}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  {!readOnly && (
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(item)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingItem(item)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={!!deletingItem} onOpenChange={(open) => !open && setDeletingItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o item "{deletingItem?.produto_nome}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
